/**
 * The two programs the graphics card runs to draw the shapes (GLSL ES 3.0, for WebGL 2).
 *
 * - The **vertex shader** places one small square per shape (an instance, see `instances.ts`),
 *   turned along the shape's x axis and a little bigger than the shape (for its outline and
 *   the soft edge).
 * - The **fragment shader** runs for every pixel of that square: it measures the signed
 *   distance from the pixel to the shape's edge (negative inside) and turns it into how much of
 *   the fill and of the outline cover the pixel. Edges get one device pixel of softening, like
 *   Canvas 2D's anti-aliasing. Colours are premultiplied by their opacity.
 *
 * The shape numbers are those of `ShapeKind` in `instances.ts`.
 */

/** Places the square of each shape. */
export const VERTEX_SHADER = `#version 300 es
layout(location = 0) in vec2 a_corner;   // -1 or 1, -1 or 1: a corner of the square
layout(location = 1) in vec4 a_box;      // centre x, y; half width, half height
layout(location = 2) in vec4 a_axis;     // x axis (cos, sin); shape; first setting
layout(location = 3) in vec2 a_extra;    // second setting; outline width
layout(location = 4) in vec4 a_fill;
layout(location = 5) in vec4 a_stroke;

uniform vec2 u_size;                     // the canvas in CSS pixels

out vec2 v_local;                        // the pixel in the shape's frame (CSS pixels)
flat out vec4 v_shape;                   // half width, half height, shape, first setting
flat out vec2 v_extra;
flat out vec4 v_fill;
flat out vec4 v_stroke;

void main() {
  float kind = a_axis.z;
  float line = kind == 6.0 ? a_axis.w : 0.0;
  vec2 ext = a_box.zw + 0.5 * max(a_extra.y, line) + 1.5;
  if (kind == 5.0) ext.x += a_box.w;     // round ends stick out by half the width
  vec2 local = a_corner * ext;
  vec2 world = a_box.xy + vec2(local.x * a_axis.x - local.y * a_axis.y, local.x * a_axis.y + local.y * a_axis.x);
  vec2 clip = world / u_size * 2.0 - 1.0;
  gl_Position = vec4(clip.x, -clip.y, 0.0, 1.0);
  v_local = local;
  v_shape = vec4(a_box.zw, kind, a_axis.w);
  v_extra = a_extra;
  v_fill = a_fill;
  v_stroke = a_stroke;
}
`;

/** Colours each pixel from its distance to the shape's edge. */
export const FRAGMENT_SHADER = `#version 300 es
precision highp float;

in vec2 v_local;
flat in vec4 v_shape;
flat in vec2 v_extra;
flat in vec4 v_fill;
flat in vec4 v_stroke;

uniform float u_soft;                    // one device pixel, in CSS pixels

out vec4 outColor;

// Signed distances (negative inside), from Inigo Quilez's catalogue.
float roundBox(vec2 p, vec2 b, float r) {
  vec2 q = abs(p) - b + r;
  return length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - r;
}

float hexagon(vec2 p, float r) {         // flat sides at the top and bottom; r: to the sides
  const vec3 k = vec3(-0.866025404, 0.5, 0.577350269);
  p = abs(p);
  p -= 2.0 * min(dot(k.xy, p), 0.0) * k.xy;
  p -= vec2(clamp(p.x, -k.z * r, k.z * r), r);
  return length(p) * sign(p.y);
}

float segment(vec2 p, vec2 a, vec2 b) {
  vec2 pa = p - a, ba = b - a;
  float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0);
  return length(pa - ba * h);
}

float triangle(vec2 p, vec2 p0, vec2 p1, vec2 p2) {
  vec2 e0 = p1 - p0, e1 = p2 - p1, e2 = p0 - p2;
  vec2 v0 = p - p0, v1 = p - p1, v2 = p - p2;
  vec2 pq0 = v0 - e0 * clamp(dot(v0, e0) / dot(e0, e0), 0.0, 1.0);
  vec2 pq1 = v1 - e1 * clamp(dot(v1, e1) / dot(e1, e1), 0.0, 1.0);
  vec2 pq2 = v2 - e2 * clamp(dot(v2, e2) / dot(e2, e2), 0.0, 1.0);
  float s = sign(e0.x * e2.y - e0.y * e2.x);
  vec2 d = min(min(vec2(dot(pq0, pq0), s * (v0.x * e0.y - v0.y * e0.x)),
                   vec2(dot(pq1, pq1), s * (v1.x * e1.y - v1.y * e1.x))),
                   vec2(dot(pq2, pq2), s * (v2.x * e2.y - v2.y * e2.x)));
  return -sqrt(d.x) * sign(d.y);
}

// Whether the point t along a dashed line falls in a gap.
bool inGap(float t, float on, float off) {
  return on > 0.0 && mod(t, on + off) > on;
}

void main() {
  vec2 p = v_local;
  vec2 h = v_shape.xy;
  float kind = v_shape.z;
  float first = v_shape.w;
  float second = v_extra.x;
  float width = v_extra.y;
  float d;
  if (kind == 0.0) d = length(p) - h.x;
  else if (kind == 1.0) d = roundBox(p, h, min(first, min(h.x, h.y)));
  else if (kind == 2.0) d = hexagon(p.yx, h.x * 0.866025404);
  else if (kind == 3.0) d = triangle(p, vec2(h.x, 0.0), vec2(-h.x, -h.y), vec2(-h.x, h.y));
  else if (kind == 4.0) d = max(abs(p.x) - h.x, abs(p.y) - h.y);
  else if (kind == 5.0) d = length(vec2(max(abs(p.x) - h.x, 0.0), p.y)) - h.y;
  else d = min(segment(p, vec2(h.x, 0.0), vec2(-h.x, h.y)), segment(p, vec2(h.x, 0.0), vec2(-h.x, -h.y))) - first * 0.5;

  if ((kind == 4.0 || kind == 5.0) && inGap(p.x + h.x, first, second)) discard;

  vec4 color = v_fill * clamp(0.5 - d / u_soft, 0.0, 1.0);
  if (width > 0.0) {
    float cover = clamp(0.5 - (abs(d) - width * 0.5) / u_soft, 0.0, 1.0);
    // A dashed ring: measured along the circle.
    if (kind == 0.0 && inGap((atan(p.y, p.x) + 3.14159265) * h.x, first, second)) cover = 0.0;
    color = v_stroke * cover + color * (1.0 - v_stroke.a * cover);
  }
  if (color.a <= 0.0) discard;
  outColor = color;
}
`;
